import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SelectInput } from 'components/Inputs';
import Button from 'components/Button';
import { get } from 'api';
import { SuccessToast, ErrorToast } from 'utils/Toasts';
import ExcelExport from 'utils/ExcelExport';
import SpinningBox from 'components/SpinningBox';

const Emails = () => {
  const theme = useTheme();
  const successToastRef = useRef();
  const errorToastRef = useRef();
  const [open, setOpen] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  // State for filter selections
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedRound, setSelectedRound] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedGender, setSelectedGender] = useState('');
  
  // State for available options based on selections
  const [availableStages, setAvailableStages] = useState([]);
  const [availableRounds, setAvailableRounds] = useState([]);
  const [availableLevels, setAvailableLevels] = useState([]);
  const [availableGenders, setAvailableGenders] = useState([]);
  
  // State for filtered data
  const [filteredData, setFilteredData] = useState(null);
  const [participantCount, setParticipantCount] = useState(0);

  // Fetch tournaments on mount
  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        const response = await get('/tournaments/tournaments');
        const data = await response.json();
        setTournaments(data);
      } catch (err) {
        console.error('Error fetching tournaments:', err);
        errorToastRef.current?.showError('Nepavyko užkrauti turnyrų sąrašo');
      }
    };
    
    fetchTournaments();
  }, []);

  // Reset dependent filters when tournament changes
  useEffect(() => {
    if (selectedTournament) {
      setSelectedStage('');
      setSelectedRound('');
      setSelectedLevel('');
      setSelectedGender('');
      setFilteredData(null);
      setParticipantCount(0);
      
      // Set available stages for all tournaments
      setAvailableStages([
        { value: 'Grupės', label: 'Grupės' },
        { value: 'Pagrindas', label: 'Pagrindas' },
        { value: 'Paguoda', label: 'Paguoda' },
      ]);
    }
  }, [selectedTournament]);

  // Update available rounds based on stage selection
  useEffect(() => {
    if (selectedStage) {
      setSelectedRound('');
      setSelectedLevel('');
      setSelectedGender('');
      
      if (selectedStage === 'Grupės') {
        setAvailableRounds([]);
      } else if (selectedStage === 'Pagrindas') {
        setAvailableRounds([
          { value: '', label: '-- Visi raundai --' },
          { value: 'P1_rez', label: 'P1' },
          { value: 'P2_rez', label: 'P2' },
          { value: 'P3_rez', label: 'P3' },
          { value: 'P4_rez', label: 'P4' },
          { value: 'P5_rez', label: 'P5' },
          { value: 'P6_rez', label: 'P6' },
          { value: 'P7_rez', label: 'P7' },
        ]);
      } else if (selectedStage === 'Paguoda') {
        setAvailableRounds([
          { value: '', label: '-- Visi raundai --' },
          { value: 'P2_rez', label: 'P2' },
          { value: 'P3_rez', label: 'P3' },
          { value: 'P4_rez', label: 'P4' },
          { value: 'P5_rez', label: 'P5' },
          { value: 'P6_rez', label: 'P6' },
          { value: 'P7_rez', label: 'P7' },
        ]);
      }
      
      // Set hardcoded levels (these are fixed and won't change)
      setAvailableLevels([
        { value: '', label: '-- Visi lygiai --' },
        { value: 'Super Light', label: 'Super Light' },
        { value: 'Light', label: 'Light' },
        { value: 'Middle', label: 'Middle' },
        { value: 'Power', label: 'Power' },
        { value: 'Hard', label: 'Hard' },
      ]);
      
      // Set available genders
      setAvailableGenders([
        { value: '', label: '-- Abi lytys --' },
        { value: 'Vyrai', label: 'Vyrai' },
        { value: 'Moterys', label: 'Moterys' },
      ]);
    }
  }, [selectedStage]);

  const handleOpen = (id) => {
    setOpen({ ...open, [id]: true });
  };

  const handleClose = (id) => {
    setOpen({ ...open, [id]: false });
  };

  const handleTournamentChange = (event) => {
    setSelectedTournament(event.target.value);
  };

  const handleStageChange = (event) => {
    setSelectedStage(event.target.value);
  };

  const handleRoundChange = (event) => {
    const value = event.target.value;
    setSelectedRound(value === '' ? '' : value);
  };

  const handleLevelChange = (event) => {
    const value = event.target.value;
    setSelectedLevel(value === '' ? '' : value);
  };

  const handleGenderChange = (event) => {
    const value = event.target.value;
    setSelectedGender(value === '' ? '' : value);
  };

  const handleLoadData = async () => {
    if (!selectedTournament) {
      errorToastRef.current?.showError('Prašome pasirinkti turnyrą');
      return;
    }

    if (!selectedStage) {
      errorToastRef.current?.showError('Prašome pasirinkti etapą');
      return;
    }

    setIsLoading(true);
    
    try {
      // Build query params
      const params = new URLSearchParams();
      params.append('tournament_id', selectedTournament);
      if (selectedStage) params.append('stage', selectedStage);
      if (selectedRound) params.append('round', selectedRound);
      if (selectedLevel) params.append('level', selectedLevel);
      if (selectedGender) params.append('gender', selectedGender);

      const response = await get(`/tournaments/filtered-participants?${params.toString()}`);
      const data = await response.json();
      
      if (data.status === 'SUCCESS') {
        setFilteredData(data.data);
        setParticipantCount(data.data.length);
        successToastRef.current?.showSuccess('Duomenys sėkmingai užkrauti');
      } else {
        errorToastRef.current?.showError(data.err || 'Nepavyko užkrauti duomenų');
      }
    } catch (err) {
      console.error('Error loading filtered data:', err);
      errorToastRef.current?.showError('Įvyko klaida užkraunant duomenis');
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectionSummary = () => {
    // Get tournament info from filteredData (which includes city) or fallback to tournaments list
    let tournamentDisplay = '';
    if (filteredData && filteredData.length > 0) {
      const tournamentName = filteredData[0].tournament_name;
      const city = filteredData[0].city;
      tournamentDisplay = `${tournamentName} (${city})`;
    } else {
      const selectedTournamentObj = tournaments.find(t => t.id === parseInt(selectedTournament));
      tournamentDisplay = selectedTournamentObj 
        ? `${selectedTournamentObj.tournament_name} (${selectedTournamentObj.city})`
        : '';
    }
    
    const parts = [];
    parts.push({ label: 'Turnyras', value: tournamentDisplay });
    if (selectedStage) parts.push({ label: 'Etapas', value: selectedStage });
    if (selectedRound) parts.push({ label: 'Raundas', value: selectedRound.replace('_rez', '') });
    if (selectedLevel) parts.push({ label: 'Lygis', value: selectedLevel });
    if (selectedGender) parts.push({ label: 'Lytis', value: selectedGender });
    
    // Add "All" indicators for unselected optional filters
    if (selectedStage && (selectedStage === 'Pagrindas' || selectedStage === 'Paguoda') && !selectedRound) {
      parts.push({ label: 'Raundas', value: 'Visi' });
    }
    if (selectedStage && !selectedLevel) {
      parts.push({ label: 'Lygis', value: 'Visi' });
    }
    if (selectedStage && !selectedGender) {
      parts.push({ label: 'Lytis', value: 'Abi' });
    }
    
    return parts;
  };

  return (
    <Box
      sx={{
        maxWidth: { xs: '100%', sm: '100%', md: '516px' },
        width: '100%',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SuccessToast ref={successToastRef} />
      <ErrorToast ref={errorToastRef} />

      {/* Tournament Selection */}
      <Box
        display={'flex'}
        flexDirection={'column'}
        maxWidth={{ xs: '100%', sm: '100%' }}
        marginTop={'24px'}
      >
        <Typography
          variant="body4"
          fontWeight={500}
          color={theme.palette.darkGrey[700]}
        >
          Turnyras: *
        </Typography>
        <SelectInput
          name="tournament"
          id="tournament"
          placeholder="Pasirinkite turnyrą"
          open={open}
          handleOpen={handleOpen}
          handleClose={handleClose}
          handleChange={handleTournamentChange}
          topMargin={'6px'}
          value={selectedTournament}
          options={tournaments.map(t => ({
            value: t.id,
            label: `${t.tournament_name} (${t.city})`,
          }))}
        />
      </Box>

      {/* Stage Selection */}
      {selectedTournament && (
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            Etapas: *
          </Typography>
          <SelectInput
            name="stage"
            id="stage"
            placeholder="Pasirinkite etapą"
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleStageChange}
            topMargin={'6px'}
            value={selectedStage}
            options={availableStages}
          />
        </Box>
      )}

      {/* Round Selection */}
      {selectedStage && availableRounds.length > 0 && (
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            Raundas:
          </Typography>
          <SelectInput
            name="round"
            id="round"
            placeholder="Pasirinkite raundą (neprivaloma)"
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleRoundChange}
            topMargin={'6px'}
            value={selectedRound}
            options={availableRounds}
          />
        </Box>
      )}

      {/* Level Selection */}
      {selectedStage && (
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            Lygis:
          </Typography>
          <SelectInput
            name="level"
            id="level"
            placeholder="Pasirinkite lygį (neprivaloma)"
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleLevelChange}
            topMargin={'6px'}
            value={selectedLevel}
            options={availableLevels}
          />
        </Box>
      )}

      {/* Gender Selection */}
      {selectedStage && (
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            Lytis:
          </Typography>
          <SelectInput
            name="gender"
            id="gender"
            placeholder="Pasirinkite lytį (neprivaloma)"
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleGenderChange}
            topMargin={'6px'}
            value={selectedGender}
            options={availableGenders}
          />
        </Box>
      )}

      {/* Load Data Button */}
      <Box
        sx={{
          marginTop: '32px',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <Button
          maxWidth={{ xs: '100%', md: '100%' }}
          width={'100%'}
          buttontype={'orange'}
          size={'small'}
          onClick={handleLoadData}
          disabled={!selectedTournament || !selectedStage || isLoading}
        >
          {isLoading ? <SpinningBox /> : 'Užkrauti duomenis'}
        </Button>
      </Box>

      {/* Selection Summary */}
      {filteredData && (
        <Box
          sx={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: theme.palette.grey[100],
            borderRadius: '8px',
          }}
        >
          <Typography
            variant="body4"
            fontWeight={600}
            color={theme.palette.darkGrey[700]}
            sx={{ marginBottom: '8px' }}
          >
            Pasirinkimas:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
            {getSelectionSummary().map((item, index) => (
              <Box key={index} sx={{ display: 'flex', flexDirection: 'row' }}>
                <Typography
                  variant="body4"
                  fontWeight={500}
                  color={theme.palette.darkGrey[700]}
                  sx={{ minWidth: '80px' }}
                >
                  {item.label}:
                </Typography>
                <Typography
                  variant="body4"
                  color={theme.palette.darkGrey[600]}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography
            variant="body4"
            fontWeight={600}
            color={theme.palette.orange[600]}
          >
            Dalyvių skaičius: {participantCount}
          </Typography>
        </Box>
      )}

      {/* Export Button */}
      {filteredData && (
        <Box sx={{ marginTop: '16px' }}>
          <ExcelExport
            buttonTitle="Eksportuoti į XLSX"
            excelData={filteredData}
            fileName={`Tournament_Participants_${selectedTournament}_${Date.now()}`}
            disabled={!filteredData || filteredData.length === 0}
          />
        </Box>
      )}
    </Box>
  );
};

export default Emails;
